import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MessageSquare, LayoutPanelLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import { CompanyLogo, Skeleton, UserAvatar } from "@/components/common";
import { StepRail, stepIndex } from "@/components/workspace/StepRail";
import { Chat } from "@/components/workspace/Chat";
import { Sheet, UnderstandPanel, DesignPanel, CalculatePanel, ProposePanel, SendPanel, EmptyCanvas } from "@/components/workspace/Canvas";

function reachedStep(ctx) {
  const q = ctx?.quote;
  if (q && q.status !== "draft") return "send";
  if (q) return "propose";
  if (ctx?.solution && (ctx.solution.groups || []).some((g) => (g.items || []).length)) return "calculate";
  return "understand";
}

/**
 * The AI Workspace session: a focused, full-viewport surface.
 * Chat steers (left) - the canvas is the commercial truth (right). Nothing on this page scrolls
 * except the message list and, where unavoidable, a list inside the canvas sheet.
 */
export default function WorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isMd = useMediaQuery("(min-width: 768px)");
  const isLg = useMediaQuery("(min-width: 1024px)");
  const [session, setSession] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [mobileView, setMobileView] = useState("chat"); // chat | canvas (below lg)
  const initialSent = useRef(false);

  useEffect(() => {
    setError(null);
    api
      .get(`/workspace/${id}`)
      .then((r) => {
        setSession(r.data.session);
        setCtx(r.data.context);
      })
      .catch((e) => setError(errMsg(e)));
  }, [id]);

  const apply = useCallback((r) => {
    setSession(r.data.session);
    setCtx(r.data.context);
  }, []);

  const send = useCallback(
    async (content) => {
      if (!content?.trim() || busy) return;
      const optimistic = { id: `tmp-${Date.now()}`, role: "user", content, created_at: new Date().toISOString() };
      setSession((s) => (s ? { ...s, messages: [...(s.messages || []), optimistic] } : s));
      setBusy(true);
      setMobileView("chat");
      try {
        const r = await api.post(`/workspace/${id}/message`, { content, origin: window.location.origin });
        apply(r);
      } catch (e) {
        toast.error(errMsg(e));
        setSession((s) => (s ? { ...s, messages: (s.messages || []).filter((m) => m.id !== optimistic.id) } : s));
      } finally {
        setBusy(false);
      }
    },
    [id, busy, apply]
  );

  const act = useCallback(
    async (type, params) => {
      if (busy) return;
      setBusy(true);
      try {
        const r = await api.post(`/workspace/${id}/action`, { type, params: params || {}, origin: window.location.origin });
        apply(r);
        if (type !== "go_to_step" && r.data.message) setMobileView("canvas");
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setBusy(false);
      }
    },
    [id, busy, apply]
  );

  // first message handed over from the start page
  useEffect(() => {
    const initial = location.state?.initial;
    if (session && initial && !initialSent.current) {
      initialSent.current = true;
      navigate(location.pathname, { replace: true, state: null });
      send(initial);
    }
  }, [session, location, navigate, send]);

  const step = session?.step || "understand";
  const reached = useMemo(() => reachedStep(ctx), [ctx]);
  const askInChat = (q) => send(`About "${q}" - `);
  const goTo = (s) => s !== step && act("go_to_step", { step: s });

  if (error) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background p-8" data-testid="workspace-error">
        <div className="max-w-sm text-center">
          <div className="font-display text-[20px] font-semibold tracking-[-0.02em]">Workspace not found</div>
          <p className="mt-2 text-[14px] text-muted-foreground">{error}</p>
          <Link to="/workspace" className="mt-5 inline-flex h-10 items-center gap-2 rounded-full bg-[hsl(var(--ink))] px-4 text-[13px] font-medium text-white hover:bg-black focus-visible:ring-2 focus-visible:ring-ring/50" data-testid="workspace-error-back-link">Back to Workspace</Link>
        </div>
      </div>
    );
  }

  const canvas = !ctx ? null : !ctx.opportunity ? (
    <EmptyCanvas onSay={send} />
  ) : step === "understand" ? (
    <UnderstandPanel ctx={ctx} act={act} busy={busy} onAsk={askInChat} />
  ) : step === "design" ? (
    <DesignPanel ctx={ctx} act={act} busy={busy} />
  ) : step === "calculate" ? (
    <CalculatePanel ctx={ctx} act={act} busy={busy} />
  ) : step === "propose" ? (
    <ProposePanel ctx={ctx} act={act} busy={busy} onSay={send} />
  ) : (
    <SendPanel ctx={ctx} act={act} busy={busy} session={session} />
  );

  return (
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-background" data-testid="workspace-page">
      {/* header - no divider, just rhythm */}
      <header className="grid h-14 shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 sm:px-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]" data-testid="workspace-header">
        <div className="flex min-w-0 items-center gap-2">
          <Link to="/workspace" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Back to Workspace" data-testid="workspace-back-link">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          {ctx?.opportunity ? (
            <div className="flex min-w-0 items-center gap-2.5">
              <CompanyLogo name={ctx.account?.name} domain={ctx.account?.website} size="sm" className="h-7 w-7 rounded-[8px] p-1" />
              <div className="min-w-0 truncate text-[13.5px]" data-testid="workspace-title">
                <span className="font-semibold">{ctx.account?.name}</span>
                <span className="mx-1.5 text-muted-foreground/50">/</span>
                <span className="text-foreground/80">{ctx.opportunity.name}</span>
              </div>
            </div>
          ) : ctx ? (
            <div className="text-[13.5px] font-semibold" data-testid="workspace-title">New workspace</div>
          ) : (
            <Skeleton className="h-4 w-44" />
          )}
        </div>
        {isMd ? (
          <div className="flex justify-center">
            <StepRail step={step} reached={reached} dense onSelect={goTo} />
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-1.5">
          {!isLg ? (
            <div className="inline-flex rounded-full bg-black/[0.045] p-1" role="tablist" aria-label="View">
              {[
                ["chat", "Chat", MessageSquare],
                ["canvas", "Canvas", LayoutPanelLeft],
              ].map(([k, label, Icon]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={mobileView === k}
                  onClick={() => setMobileView(k)}
                  className={cn("flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-medium text-muted-foreground transition-[color,background-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-ring/40", mobileView === k && "bg-white text-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.08),0_0_0_1px_hsl(30_12%_18%/0.05)]")}
                  data-testid={`workspace-view-${k}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          ) : null}
          {ctx?.opportunity && isMd ? (
            <Link to={`/opportunities/${ctx.opportunity.id}`} className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-[12.5px] font-medium text-muted-foreground hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="workspace-header-opportunity-link">
              Opportunity <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          ) : null}
          <Link to="/settings" className="ml-1 rounded-full ring-offset-2 ring-offset-background transition-transform duration-150 hover:scale-[1.04] focus-visible:ring-2 focus-visible:ring-ring/50" aria-label="Profile and settings" data-testid="workspace-profile-link">
            <UserAvatar name={user?.name} initials={user?.initials} color={user?.color} src={user?.avatar_url} size="md" className="h-8 w-8" testId="workspace-user-avatar" />
          </Link>
        </div>
      </header>
      {!isMd ? (
        <div className="flex shrink-0 justify-center px-3 pb-2">
          <StepRail step={step} reached={reached} dense onSelect={goTo} />
        </div>
      ) : null}

      {/* body: chat | canvas */}
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[400px_minmax(0,1fr)] xl:grid-cols-[440px_minmax(0,1fr)] 2xl:grid-cols-[480px_minmax(0,1fr)]">
        {isLg || mobileView === "chat" ? (
          <section className="flex min-h-0 min-w-0 flex-col" data-testid="workspace-chat-pane" aria-label="Chat">
            {!session ? (
              <div className="space-y-3 p-6">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : (
              <Chat messages={session.messages || []} step={step} busy={busy} onSend={send} className="h-full" placeholder={!ctx?.opportunity ? "Paste an inquiry, e-mail or meeting notes…" : undefined} />
            )}
          </section>
        ) : null}
        {isLg || mobileView === "canvas" ? (
          <section className="min-h-0 min-w-0 p-2 pt-0 lg:p-3 lg:pl-0 lg:pt-0" data-testid="workspace-canvas" aria-label="Canvas">
            <div className="mx-auto h-full w-full max-w-[1320px]">
              {!ctx ? (
                <Sheet testId="workspace-canvas-loading">
                  <div className="space-y-4 p-8">
                    <Skeleton className="h-5 w-1/3" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-24" />
                    <Skeleton className="h-24" />
                  </div>
                </Sheet>
              ) : (
                <div key={step} className="sheet-enter h-full">{canvas}</div>
              )}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

export { stepIndex };
